<template>
    <label class="pr f fv mead-form-textarea" :for="uid">
        <span class="f fh fa-c dw-10 el-frm-lbl" v-if="lbl">
            <span class="f fh fj-sb fg">
                <span class="fg">{{ uiTitle }}</span>
                <span v-if="uiReset" class="te-link c-p" @click.prevent="reset">{{ 'mead.reset' | athT }}</span>
            </span>
        </span>
        <div class="pr el-frm-textarea">
            <textarea ref="input"
                novalidate
                class="dw-10 el-frm-input"
                :class="[uiBorder ? 'el--bordered' : '']"
                cols="1"
                rows="1"
                @input="update"
                v-model="data"
                :name="uid"
                :id="uid"
                :disabled="disabled"
                :placeholder="placeholder | athT"
                @keydown.enter="onKeyDownEnter">
            </textarea>
            <small class="pa-br el-frm-counter"
                v-if="maxlength !== false && ui.remaining < (maxlength * .9)"
                :class="[
                    ui.remaining < 50 && ui.remaining > 15 ? 'el--t-warning' : '',
                    ui.remaining <= 15 ? 'el--t-danger' : '',
                    ui.remaining < 0 ? 'el--t-error' : '',
                ]">{{ ui.remaining }}<template v-if="uiShowMaxLength"> / {{ maxlength }}</template></small>
        </div>
        <div class="f fh fa-c dw-10 sm-t-xs" v-if="uiActionsList">
            <mead-button-icon v-if="uiActionEmoji"
                class="sm-r-xs el--s-s el--transparent"
                icon="smile"
                title="mead.labels.add_emoji"
                @click="actEmoji" />
            <!-- @slot Extra action buttons shown below the textarea, after the emoji button -->
            <slot name="actionsAfter"></slot>
        </div>
    </label>
</template>

<script>
import { guuid, join } from '@aretheyhappy/utils';

export default {
    name: 'mead-form-textarea',
    props: {
        /**
         * Label i18n key rendered above the textarea. Only shown when truthy.
         */
        lbl         : {type: [String, Boolean], default: () => false},
        /**
         * Placeholder text (i18n key) shown in the textarea when empty.
         */
        placeholder : {type: [String, Boolean], default: () => false},
        /**
         * Current text content (v-model).
         */
        value       : {type: String, default: () => ''},
        /**
         * Disables the textarea.
         */
        disabled    : {type: Boolean, default: () => false},
        /**
         * i18n key applied as the native `title`/tooltip attribute on the textarea.
         */
        title       : {type: [String, Boolean], default: () => false},
        /**
         * Maximum character length allowed; also enables the remaining-character counter.
         */
        maxlength   : {type: [Number, Boolean], default: () => false},
        /**
         * Flash pubsub key that lets external code inject text at the last cursor position or focus
         * the textarea (via `${pubkey}:inject_at_cursor` / `${pubkey}:focus` events).
         */
        pubkey      : {type: [Boolean, String], default: () => false},
        /**
         * Whether to run emoji shortcut parsing (e.g. `:smile:`) on the entered text.
         */
        uiEmoji     : {type: Boolean, default: () => true},
        /**
         * Minimum number of visible rows the textarea auto-sizes down to.
         */
        uiMinRows   : {type: Number, default: () => 1},
        /**
         * Maximum number of rows the textarea auto-sizes up to before scrolling.
         */
        uiMaxRows   : {type: Number, default: () => 10},
        /**
         * Appends an "(optional)" suffix to the label.
         */
        uiOptional  : {type: Boolean, default: () => false},
        /**
         * Shows a "Reset" link next to the label that clears the text. Needs `lbl`.
         */
        uiReset     : {type: Boolean, default: () => false},
        /**
         * Draws a border around the textarea.
         */
        uiBorder    : {type: Boolean, default: () => false},
        /**
         * Shows the counter as "remaining / max" instead of just the remaining characters. Needs `maxlength`.
         */
        uiShowMaxLength : {type: Boolean, default: () => false},
        /**
         * Shows an emoji button below the textarea. Mead has no emoji picker, so clicking it emits
         * `emoji-click` with the click event, and the consumer opens its own picker. A picker that publishes
         * `${pubkey}:emoji` with `{c: '😀'}` has the emoji inserted at the cursor. Needs `pubkey`.
         */
        uiActionEmoji : {type: Boolean, default: () => false},
        /**
         * Shows the row of action buttons below the textarea, for the `actionsAfter` slot.
         * The row also shows whenever `uiActionEmoji` is on or the slot has content.
         */
        uiHasActions : {type: Boolean, default: () => false},
        /**
         * Pressing Enter without Shift emits `onEnterPress` instead of adding a new line, e.g. for a chat
         * box. Shift+Enter still adds a new line. Off by default, so Enter adds a new line.
         */
        uiSubmitOnEnter : {type: Boolean, default: () => false},
        /**
         * Function invoked when the textarea receives focus, attached as a native `focus` listener.
         */
        uiOnFocus   : {type: [Boolean, Function], default: () => false},
        /**
         * Automatically focuses the textarea once mounted.
         */
        uiAutoFocus : {type: Boolean, default: () => false},
    },
    data () {
        return {
            data: (this.value || '').trim(),
            uid : guuid(),
            ui  : {
                line_height: false,
                cursor_pos: 0,
                //  Count from the starting value, so the counter is right before the first keystroke
                remaining: this.maxlength !== false ? this.maxlength - (this.value || '').trim().length : 0,
            },
        };
    },
    methods: {
        resize: function () {
            //  Wait for v-model to update the textarea before measuring it
            this.$nextTick(() => {
                if (!this.$refs.input) return;

                //  Reset rows to be able to calculate scrollHeight
                this.$refs.input.rows = 1;

                let rows = this.$refs.input.scrollHeight !== 0 ? Math.floor(this.$refs.input.scrollHeight/this.ui.line_height) : 1;

                if (rows < 1 || this.data.trim().length === 0) rows = 1;

                if (rows > this.uiMaxRows) {
                    this.$refs.input.rows = this.uiMaxRows;
                } else {
                    this.$refs.input.rows = rows < this.uiMinRows ? this.uiMinRows : rows;
                }

                this.$refs.input.style.overflowY = 'auto';
            });
        },
        update: async function () {
            //  If emoji is enabled on the textarea, run shortcut analysis
            if (this.uiEmoji) {
                await MeadCommon.UIEmoji.analyzeText(this.data).then(obj => {
                    if (!obj.matched) return;
                    this.data = obj.parsed;
                });
            }

            //  Calculate remaining characters if maxlength is passed
            if (this.maxlength !== false) {
                this.ui.remaining = this.maxlength - (this.data || '').length;
            }

            this.$emit('input', this.data);
            this.resize();
        },
        updateCursorPos: function () {
            this.ui.cursor_pos = this.$refs.input.selectionStart;
        },
        onKeyDownEnter: function (event) {
            if (!this.uiSubmitOnEnter || event.shiftKey) return;

            event.preventDefault();

            /**
             * Enter was pressed without Shift. Only emitted when `uiSubmitOnEnter` is on.
             */
            this.$emit('onEnterPress');
        },
        actEmoji: function (event) {
            /**
             * The emoji button was clicked. Open an emoji picker from here, passing it `pubkey`.
             * @property {MouseEvent} event The click event, to anchor a popover to
             */
            this.$emit('emoji-click', event);
        },
        reset: function () {
            this.data = '';
            this.update();
        },
    },
    watch: {
        value: function (new_val, old_val) {
            if ((new_val === old_val) || (new_val === this.data)) return;
            this.data = this.value;
            this.update();
        },
    },
    computed: {
        uiActionsList: function () {
            return this.uiActionEmoji || this.uiHasActions || !!this.$slots.actionsAfter;
        },
        uiTitle: function () {
            return join([
                MeadCommon.I18N.go(this.lbl),
                this.uiOptional ? `(${MeadCommon.I18N.go('mead.labels.optional')})` : false,
            ], {delim: ' '});
        },
    },
    mounted () {
        const compstyle = getComputedStyle(this.$refs.input);

        //  Cache defaults so we can easily access them without too many dom checks
        this.ui.line_height = parseInt(compstyle.lineHeight);

        //  Set value back to original
        this.resize();

        //  Add title
        if (this.title) this.$refs.input.setAttribute('title', MeadCommon.I18N.go(this.title));

        //  UIAutoFocus
        if (this.$refs.input && this.uiAutoFocus) this.$refs.input.focus();

        //  Add uiOnFocus
        if (this.uiOnFocus) this.$refs.input.addEventListener('focus', this.uiOnFocus);

        //  Add blur handler
        this.$refs.input.addEventListener('blur', this.updateCursorPos);
        this.$refs.input.addEventListener('focus', this.updateCursorPos);
    },
    created () {
        if (this.pubkey) {
            //  On inject at cursor event (text injection where the cursor was last)
            MeadCommon.Flash.subscribe(this.uid, `${this.pubkey}:inject_at_cursor`, txt => {
                const val = this.$refs.input.value;
                this.$refs.input.value = `${val.slice(0, this.ui.cursor_pos)}${txt}${val.slice(this.ui.cursor_pos)}`;
                MeadCommon.DomUtils.placeCaret(this.$refs.input, this.ui.cursor_pos + txt.length);

                this.data = this.$refs.input.value;

                this.update();
                this.updateCursorPos();
            });

            //  On focus event, focus input and set caret
            MeadCommon.Flash.subscribe(this.uid, `${this.pubkey}:focus`, () => {
                this.$refs.input.focus();
                MeadCommon.DomUtils.placeCaret(this.$refs.input, this.ui.cursor_pos);
            });

            //  On emoji picked (e.g. from the consumer's picker opened by `emoji-click`), insert it at the cursor
            MeadCommon.Flash.subscribe(this.uid, `${this.pubkey}:emoji`, o => {
                MeadCommon.Flash.publish(`${this.pubkey}:inject_at_cursor`, o.c);
            });
        }
    },
    destroyed () {
        MeadCommon.Flash.unsubscribe(this.uid);
    },
};
</script>
<style lang="scss" scoped>
@import '../../../../sass/_variables.scss';

.mead-form-textarea {
    border-bottom: 1px solid var(--c-panel-border);
    background-color: var(--c-panel-bg);

    .el-frm-input {
        font-family: $ff-be;
        font-size: $fs-m;
        line-height: $lh-s;
        word-wrap: break-word;
        resize: none;
        border: none;
        padding: $s-s 0 0 0;
        background-color: #fff;
        appearance: none;
        display: inline-block;
        color: var(--c-txt);
        text-align: left;
        max-width: 100%;
        min-width: 100%;
        box-sizing: border-box;

        &:focus,
        &:active {
            outline: none;
        }

        &:empty:before {
            content: attr(placeholder);
            color: var(--c-frm-pholder-fg);
            display: block;
        }

        &.el--disabled {
            cursor: not-allowed;
            opacity: .65;
        }

        &.el--bordered {
            border: 1px solid var(--c-panel-border);
        }
    }

    .el-frm-counter {
        font-weight: bold;
        font-size: $fs-xxs;
        line-height: $fs-xxs;
        color: var(--c-txt-light);
        background-color: #fff;
        padding-left: .2rem;
        padding-top: .2rem;

        &.el--t-warning {
            color: #ee9e2f;
        }

        &.el--t-danger {
            color: #ff0f0f;
        }
    }

    //  MODIFIERS

    &.el--standalone {
        border: none;
        border-radius: $br-m;
        background-color: transparent;

        .el-frm-lbl {
            margin-bottom: $s-xs;
        }

        .el-frm-input {
            background-color: var(--c-brand-light-bg);
            color: var(--c-brand-light-fg);
            padding: .8rem $s-s;
            border-radius: $br-m;
        }

        .el-frm-counter {
            right: .4rem;
            bottom: .4rem;
            background-color: var(--c-brand-light-bg);
        }
    }
}

</style>
